import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";

const LAST_UPDATED = "June 2, 2026";

export default function Disclaimer() {
  const { t } = useLanguage();
  usePageSEO({
    title: "Disclaimer – ISO27001KIT",
    description: "Important disclaimers about the use of ISO27001KIT templates, tools, and resources. Understand limitations and responsibilities.",
    noindex: true,
  });
  
  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 text-foreground">{t('disclaimer.title')}</h1>
        <div className="h-1 w-24 bg-primary mb-8"></div>
        
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-foreground">
          <p className="text-sm text-muted-foreground">{t('disclaimer.lastUpdated')} {LAST_UPDATED}</p>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.general.title')}</h2>
            <p>{t('disclaimer.general.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.products.title')}</h2>
            <p>{t('disclaimer.products.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>{t('disclaimer.products.freePdfs')}</strong></li>
              <li><strong>{t('disclaimer.products.proTools')}</strong></li>
              <li><strong>{t('disclaimer.products.wordPack')}</strong></li>
            </ul>
            <p>{t('disclaimer.products.note')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.professional.title')}</h2>
            <p>{t('disclaimer.professional.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.professional.legal')}</li>
              <li>{t('disclaimer.professional.consulting')}</li>
              <li>{t('disclaimer.professional.certification')}</li>
              <li>{t('disclaimer.professional.audit')}</li>
              <li>{t('disclaimer.professional.risk')}</li>
            </ul>
            <p className="mt-4">{t('disclaimer.professional.recommendation')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.noCertification.title')}</h2>
            <p>{t('disclaimer.noCertification.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.noCertification.riskAssessment')}</li>
              <li>{t('disclaimer.noCertification.controls')}</li>
              <li>{t('disclaimer.noCertification.evidence')}</li>
              <li>{t('disclaimer.noCertification.audit')}</li>
              <li>{t('disclaimer.noCertification.ongoing')}</li>
            </ul>
            <p className="mt-4">{t('disclaimer.noCertification.note')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.toolOutput.title')}</h2>
            <p>{t('disclaimer.toolOutput.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.toolOutput.starting')}</li>
              <li>{t('disclaimer.toolOutput.review')}</li>
              <li>{t('disclaimer.toolOutput.requirements')}</li>
              <li>{t('disclaimer.toolOutput.validate')}</li>
              <li>{t('disclaimer.toolOutput.substitute')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.accuracy.title')}</h2>
            <p>{t('disclaimer.accuracy.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.accuracy.accurate')}</li>
              <li>{t('disclaimer.accuracy.suitability')}</li>
              <li>{t('disclaimer.accuracy.compliance')}</li>
              <li>{t('disclaimer.accuracy.local')}</li>
              <li>{t('disclaimer.accuracy.errors')}</li>
            </ul>
            <p className="mt-4">{t('disclaimer.accuracy.note')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.customization.title')}</h2>
            <p>{t('disclaimer.customization.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.customization.context')}</li>
              <li>{t('disclaimer.customization.risk')}</li>
              <li>{t('disclaimer.customization.regulatory')}</li>
              <li>{t('disclaimer.customization.industry')}</li>
              <li>{t('disclaimer.customization.structure')}</li>
              <li>{t('disclaimer.customization.geographic')}</li>
            </ul>
            <p className="mt-4">{t('disclaimer.customization.note')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.liability.title')}</h2>
            <p>{t('disclaimer.liability.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.liability.use')}</li>
              <li>{t('disclaimer.liability.implementation')}</li>
              <li>{t('disclaimer.liability.audits')}</li>
              <li>{t('disclaimer.liability.breaches')}</li>
              <li>{t('disclaimer.liability.nonCompliance')}</li>
              <li>{t('disclaimer.liability.business')}</li>
              <li>{t('disclaimer.liability.reliance')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.payment.title')}</h2>
            <p>{t('disclaimer.payment.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.payment.sameContent')}</li>
              <li>{t('disclaimer.payment.noGuarantee')}</li>
              <li>{t('disclaimer.payment.secure')}</li>
              <li>{t('disclaimer.payment.refund')}{" "}
                <Link to="/refund-policy" className="text-primary underline underline-offset-2 hover:text-primary/80">
                  {t('terms.payments.refunds.link')}
                </Link>
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.external.title')}</h2>
            <p>{t('disclaimer.external.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.external.accuracy')}</li>
              <li>{t('disclaimer.external.privacy')}</li>
              <li>{t('disclaimer.external.services')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.noWarranties.title')}</h2>
            <p>{t('disclaimer.noWarranties.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.noWarranties.merchantability')}</li>
              <li>{t('disclaimer.noWarranties.fitness')}</li>
              <li>{t('disclaimer.noWarranties.nonInfringement')}</li>
              <li>{t('disclaimer.noWarranties.accuracy')}</li>
              <li>{t('disclaimer.noWarranties.uninterrupted')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.regulatory.title')}</h2>
            <p>{t('disclaimer.regulatory.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.updates.title')}</h2>
            <p>{t('disclaimer.updates.content')}</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.userResponsibility.title')}</h2>
            <p>{t('disclaimer.userResponsibility.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('disclaimer.userResponsibility.evaluate')}</li>
              <li>{t('disclaimer.userResponsibility.advice')}</li>
              <li>{t('disclaimer.userResponsibility.review')}</li>
              <li>{t('disclaimer.userResponsibility.risks')}</li>
              <li>{t('disclaimer.userResponsibility.comply')}</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('disclaimer.contact.title')}</h2>
            <p>{t('disclaimer.contact.intro')}</p>
            <p className="font-semibold">
              {t('disclaimer.contact.email')}{" "}
              <a href="mailto:contact@iso27001kit.com" className="text-primary underline underline-offset-2 hover:text-primary/80">
                contact@iso27001kit.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
